import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, forwardRef, OnChanges, SimpleChanges} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormControl, FormGroup } from '@angular/forms';

import { HasSubscriptions } from '../../has-subscriptions';
import { Subject } from 'rxjs';
import { Helper } from '../../../classes/helper';
import {DomManipulationService} from '../../../services/dom-manipulation/dom-manipulation.service';
import { IonRangeSliderComponent } from 'ng2-ion-range-slider';

@Component({
  selector: 'app-min-max-slider',
  templateUrl: './min-max-slider.component.html',
  styleUrls: ['./min-max-slider.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MinMaxSliderComponent), multi: true },
  ]
})
export class MinMaxSliderComponent extends HasSubscriptions implements OnInit, OnChanges, ControlValueAccessor {
  propagateChange: any = () => {};
  validateFn: any = () => {};
  setDisabledState: any = () => {};


  @Input() parentFormGroup: FormGroup;

  @Input() unit: string;
  @Input() values: number[];
  @Input() label: string;
  // TODO: Translations

  // Component dependent, is not the actual postfix/prefix passed to the ion slider though. We use a middle man so that values can be customized
  @Input() postfix: string = ' or More';
  @Input() prefix: string = 'Less than ';
  @Input() toMagnitudes: (x) => {};
  @Input() disabled: boolean;
  @Input() _minMax: HasMinMax = {};

  // The value that we want to display when they haven't selected a value
  @Input() anyValueText: string = 'Any value';
  @Output() minMaxChange = new EventEmitter<HasMinMax>();
  @ViewChild("sliderElement", { 'read': ElementRef }) private _sliderRef: ElementRef;

  // Use this to change classes on the ion-slider, to hide or show specific things
  modifiyingClass: string;


  initialMin: number;
  initialMax: number;

  private _hardCompRef: IonRangeSliderComponent = <any>{};

  constructor(private domManipulationService: DomManipulationService) {
    super();
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    this._calcValues();
  }

  rangeChanged(a: { from, from_percent, from_value, max, min, to, to_percent, to_value }) {
    const newRange: HasMinMax = {};
    if (a.from_value !== this.values[0]) newRange.min = a.from_value;
    if (a.to_value !== this.values[this.values.length - 1]) newRange.max = a.to_value;
    this.propagateChange(newRange);
    this.minMaxChange.next(newRange);

  }

  onChange(a: { from, from_percent, from_value, max, min, to, to_percent, to_value }) {
    this._updateValues(a);
  }

  // TODO: this hurts me so
  // Use this to dynamically add classes that hide/show the relevant labels as well as putting in the custom value if necessary
  _updateValues(a: { from_percent, to_percent, to_value, from_value }) {
    // We need to manually do it like this because modifying the prefix and postfix variables into the component causes the component to reset
    // each time the prefix/postfix changes

    this.modifiyingClass = 'single-only';
    // No values selected, all values are legal
    if (a.from_percent === 0 && a.to_percent === 100 && this.domManipulationService.querySelector(this._sliderRef, '.irs-single')) {
      this.domManipulationService.querySelectorAndSetInnerHTML(this._sliderRef, '.irs-single', this.anyValueText);
    }
    // Min selected, but no max
    else if (a.from_percent > 0 && a.to_percent === 100 && this.domManipulationService.querySelector(this._sliderRef, '.irs-from')) {
      this.modifiyingClass = 'from-only';
      let val = '';
      if (this.toMagnitudes) val = this.toMagnitudes(a.from_value) + this.postfix;
      else val = a.from_value + this.postfix;
      this.domManipulationService.querySelectorAndSetInnerHTML(this._sliderRef, '.irs-from', this.unit ? val + ' ' + this.unit : val);
    }
    // Max selected, but no min
    else if (a.from_percent === 0 && a.to_percent < 100 &&
      this.domManipulationService.querySelector(this._sliderRef, '.irs-to')) {
      this.modifiyingClass = 'to-only';
      let val = '';
      if (this.toMagnitudes) val = this.prefix + this.toMagnitudes(a.to_value);
      else val =  this.prefix + a.to_value;
      this.domManipulationService.querySelectorAndSetInnerHTML(this._sliderRef, '.irs-to', this.unit ? val + ' ' + this.unit : val);
    }
    // min and max selected
    else {
      const curVal = this.domManipulationService.querySelector(this._sliderRef, '.irs-single').innerHTML;
      this.domManipulationService.querySelectorAndSetInnerHTML(this._sliderRef, '.irs-single', this.unit ? curVal + ' ' + this.unit : curVal);
    }

    // Maximum value text should be updated every time the price value is updated since the largest value is max_safe_integer
    let indexOffset = this._getIndexOffset();
    const text = this.toMagnitudes ? this.toMagnitudes(this.values[this.values.length - indexOffset]) + '+' : this.values[this.values.length - indexOffset] + '+';
    this.domManipulationService.querySelectorAndSetInnerHTML(this._sliderRef, '.irs-max', text);
  }

  get minMax() {
    return this._minMax;
  }

  set minMax(val) {
    this._minMax = val;
    this.propagateChange(this._minMax);
  }

  // Called when form is initialized, with form model's initial value
  writeValue(value: any) {
    if (value) this.minMax = value;
    this._calcValues();
  }

  // Access to a function that informs the outside world about changes
  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  // Callback whenever a form input is touched (ex: on blur, fires touch event)
  registerOnTouched() {
    // TODO: what is this
  }

  private _calcValues() {
    this.initialMin = (this.minMax.min !== undefined && this.values.indexOf(this.minMax.min)) || 0;
    this.initialMax = (this.minMax.max !== undefined && this.values.indexOf(this.minMax.max)) || this.values.length - 1;
    const toPercent = this.initialMax / (this.values.length - 1) * 100;
    // Need this so that we give a second for the page to load first, before we update it
    setTimeout(() => {
      this._updateValues({ from_percent: this.initialMin, to_percent: toPercent, from_value: this.minMax.min, to_value: this.minMax.max });
      let indexOffset = this._getIndexOffset();
      const text = this.toMagnitudes ? this.toMagnitudes(this.values[this.values.length - indexOffset]) + '+' : this.values[this.values.length - indexOffset] + '+';
      this.domManipulationService.querySelectorAndSetInnerHTML(this._sliderRef, '.irs-max', text);
    });
  }

  private _getIndexOffset() {
    return (this.values[this.values.length - 1] === Number.MAX_SAFE_INTEGER) ? 2 : 1;
  }

}

export interface HasMinMax {
  min?: number;
  max?: number;
}
